// FieldVision Admin - Order List Component v4
// Med status ikoner kolonne
import React, { useState, useEffect } from 'react';
import { getOrders, Order } from '../services/api';

// Status konfiguration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  Modtaget: { label: 'Modtaget', color: '#ca8a04', bgColor: '#fef9c3' },
  Bekræftet: { label: 'Bekræftet', color: '#16a34a', bgColor: '#dcfce7' },
  Behandles: { label: 'Behandles', color: '#2563eb', bgColor: '#dbeafe' },
  Udføres: { label: 'Udføres', color: '#9333ea', bgColor: '#f3e8ff' },
  Færdig: { label: 'Færdig', color: '#6b7280', bgColor: '#f3f4f6' },
};

const STATUSES = ['Modtaget', 'Bekræftet', 'Behandles', 'Udføres', 'Færdig'];

interface OrderListProps {
  onSelectOrder: (orderId: number) => void;
}

const OrderList: React.FC<OrderListProps> = ({ onSelectOrder }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOrders = async (status?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await getOrders(status || undefined);
      setOrders(data.orders);
      setStatusCounts(data.statusCounts);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(activeStatus || undefined);
  }, [activeStatus]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'I dag';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'I går';
    }
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusStyle = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.Færdig;
    return {
      color: config.color,
      backgroundColor: config.bgColor,
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
    };
  };

  // Render status ikoner - ingen JSX.Element type, bare direkte render
  const renderStatusIcons = (order: Order) => {
    return (
      <div style={styles.statusIcons}>
        {order.customerEmailSentAt && (
          <span
            title={`Email sendt ${formatDateTime(order.customerEmailSentAt)}`}
            style={styles.statusIcon}
          >
            📧
          </span>
        )}
        {order.customerConfirmedAt && (
          <span
            title={`Kunde bekræftet ${formatDateTime(order.customerConfirmedAt)}`}
            style={styles.statusIcon}
          >
            ✅
          </span>
        )}
        {order.pilotEmailSentAt && (
          <span
            title={`Pilot notificeret ${formatDateTime(order.pilotEmailSentAt)}`}
            style={styles.statusIcon}
          >
            👨‍✈️
          </span>
        )}
        {order.pilotConfirmedAt && (
          <span
            title={`Pilot accepteret ${formatDateTime(order.pilotConfirmedAt)}`}
            style={styles.statusIcon}
          >
            🚁
          </span>
        )}
        {order.completedAt && (
          <span
            title={`Færdig ${formatDateTime(order.completedAt)}`}
            style={styles.statusIcon}
          >
            🏁
          </span>
        )}
        {order.mapCount > 0 && (
          <span
            title={`${order.mapCount} kort uploadet`}
            style={{
              backgroundColor: '#8b5cf6',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 'bold',
              marginLeft: '4px'
            }}
          >
            KORT
          </span>
        )}
        {!order.customerEmailSentAt && !order.customerConfirmedAt && !order.pilotEmailSentAt && !order.pilotConfirmedAt && !order.completedAt && (
          <span style={styles.noIcons}>-</span>
        )}
      </div>
    );
  };

  // Filtrer orders baseret på dato
  const filteredOrders = orders.filter(order => {
    if (!fromDate && !toDate) return true;
    const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
    if (fromDate && orderDate < fromDate) return false;
    if (toDate && orderDate > toDate) return false;
    return true;
  });

  return (
    <div style={styles.container}>
      {/* Status Tabs */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveStatus(null)}
          style={{
            ...styles.tab,
            ...(activeStatus === null ? styles.tabActive : {}),
          }}
        >
          Alle ({Object.values(statusCounts).reduce((a, b) => a + b, 0)})
        </button>
        {STATUSES.map((status) => {
          const count = statusCounts[status] || 0;
          const config = STATUS_CONFIG[status];
          const isActive = activeStatus === status;

          return (
            <button
              key={status}
              onClick={() => setActiveStatus(status)}
              style={{
                ...styles.tab,
                ...(isActive ? { ...styles.tabActive, borderColor: config.color } : {}),
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: config.color,
                  display: 'inline-block',
                  marginRight: '6px',
                }}
              />
              {config.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Dato Filter */}
      <div style={styles.dateFilter}>
        <label style={styles.dateLabel}>
          Fra:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={styles.dateInput}
          />
        </label>
        <label style={styles.dateLabel}>
          Til:
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={styles.dateInput}
          />
        </label>
        {(fromDate || toDate) && (
          <button
            onClick={() => { setFromDate(''); setToDate(''); }}
            style={styles.resetButton}
          >
            Nulstil
          </button>
        )}
      </div>

      {/* Error */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Loading */}
      {loading && <div style={styles.loading}>Henter ordrer...</div>}

      {/* Order Table */}
      {!loading && filteredOrders.length === 0 && (
        <div style={styles.empty}>Ingen ordrer fundet</div>
      )}

      {!loading && filteredOrders.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ordre</th>
              <th style={styles.th}>Kunde</th>
              <th style={styles.th}>Marker</th>
              <th style={styles.th}>Areal</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Pris</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Flow</th>
              <th style={styles.th}>Dato</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr
                key={order.orderId}
                style={styles.tr}
                onClick={() => onSelectOrder(order.orderId)}
              >
                <td style={styles.td}>
                  <span style={styles.orderNumber}>#{order.orderId}</span>
                </td>
                <td style={styles.td}>
                  <div style={styles.customerName}>{order.customerName || 'Ukendt'}</div>
                  <div style={styles.customerEmail}>{order.customerEmail}</div>
                </td>
                <td style={styles.td}>{order.fieldCount} mrk</td>
                <td style={styles.td}>{order.totalArea?.toFixed(1)} ha</td>
                <td style={styles.td}>
                  <span style={styles.mapType}>{order.mapType}</span>
                </td>
                <td style={styles.td}>{Math.round(order.totalPrice).toLocaleString('da-DK')} kr</td>
                <td style={styles.td}>
                  <span style={getStatusStyle(order.status)}>{order.status}</span>
                </td>
                <td style={styles.td}>{renderStatusIcons(order)}</td>
                <td style={styles.td}>{formatDate(order.createdAt)}</td>
                <td style={styles.td}>
                  <span style={styles.arrow}>→</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#1f2937',
    backgroundColor: '#f9fafb',
    borderColor: '#16a34a',
  },
  dateFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  dateLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
  },
  dateInput: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  resetButton: {
    padding: '8px 16px',
    fontSize: '14px',
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#9ca3af',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '14px',
    color: '#1f2937',
  },
  orderNumber: {
    fontWeight: 600,
    color: '#374151',
  },
  customerName: {
    fontWeight: 500,
  },
  customerEmail: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  mapType: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  arrow: {
    color: '#9ca3af',
    fontSize: '18px',
  },
  statusIcons: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  statusIcon: {
    fontSize: '16px',
    cursor: 'help',
  },
  noIcons: {
    color: '#d1d5db',
    fontSize: '14px',
  },
};

export default OrderList;
